import { useRef } from 'react';
import {
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  useDisclosure,
  ButtonProps,
  AlertDialogProps,
} from '@chakra-ui/react';
import { Trans } from 'react-i18next';

import logger from 'logger';
import { useAppDispatch, useAppSelector } from 'store/configureStore';
import { saveStep } from 'actions/leadActions';
import { selectLeadInventory } from 'slices/leadSlice';

const STEP_NAME = 'nextSteps';

export const SkipInventoryDialog = ({
  hasInventory = false,
  isOpen,
  onConfirmFinish,
  onCancelFinish,
  onClose,
}: {
  isOpen: AlertDialogProps['isOpen'];
  onConfirmFinish(): void;
  onCancelFinish(): void;
  onClose: AlertDialogProps['onClose'];
  hasInventory?: boolean;
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogCloseButton />
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {!hasInventory ? (
              <Trans>Ready to send?</Trans>
            ) : (
              <Trans>Are you sure?</Trans>
            )}
          </AlertDialogHeader>

          <AlertDialogBody>
            {!hasInventory ? (
              <Trans>
                Please check that you have no other changes. To make changes,
                hit the “CANCEL” button to edit your information, or “SEND” to
                send your information to us.
              </Trans>
            ) : (
              <Trans>
                If you send your request now, we can no longer save the
                inventory you began. We will call you instead to collect those
                details.
              </Trans>
            )}
          </AlertDialogBody>
          <AlertDialogFooter justifyContent="space-between">
            <Button
              ref={cancelRef}
              onClick={onCancelFinish}
              variant="subtleLink"
              data-test-id="submit-cancel"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button onClick={onConfirmFinish} data-test-id="submit-finish">
              <Trans>Send</Trans>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

const SkipInventoryButton = ({ children, ...props }: ButtonProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const inventory = useAppSelector(selectLeadInventory);
  const dispatch = useAppDispatch();

  const handleFinish = async () => {
    try {
      const result = await dispatch(
        saveStep({
          stepToSave: STEP_NAME,
          lead: {
            roomsStart: false,
          },
          stepToNagivateTo: 'finish',
        }),
      ).unwrap();
      return result;
    } catch (error) {
      logger.error('onContinue error', { error });
      throw error;
    }
  };

  const handleClick = () => {
    if (inventory.length > 0) {
      return onOpen();
    }
    handleFinish();
  };

  const handleCancel = () => {
    onClose();
    dispatch(
      saveStep({
        stepToSave: STEP_NAME,
        lead: {},
        stepToNagivateTo: 'inventory',
      }),
    );
  };

  return (
    <>
      <Button {...props} onClick={handleClick}>
        {children}
      </Button>
      <SkipInventoryDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirmFinish={handleFinish}
        onCancelFinish={handleCancel}
      />
    </>
  );
};

export default SkipInventoryButton;
